import { useCallback, useEffect, useState } from 'react';

import { useDisplayMode } from '~/hooks/useDisplayMode';

import { isStandaloneDisplayMode } from './pwaPromptConfig';

const readInstalledFlag = (storageKey: string): boolean => {
  if (typeof localStorage === 'undefined') return false;

  try {
    return localStorage.getItem(storageKey) === 'true';
  } catch {
    return false;
  }
};

const writeInstalledFlag = (storageKey: string): void => {
  if (typeof localStorage === 'undefined') return;

  try {
    localStorage.setItem(storageKey, 'true');
  } catch (e) {
    console.warn('localStorage access denied:', e);
  }
};

// Standalone display mode only applies once the user opens the app from their
// home screen — not right after accepting the browser install prompt while still
// in a tab. InstallPrompt and InstallAppCallout share this hook so both hide as
// soon as install completes and stay hidden across reloads in the browser.
export const usePwaInstalled = (installedStorageKey: string) => {
  const displayMode = useDisplayMode();
  const [installedViaPrompt, setInstalledViaPrompt] = useState(() => readInstalledFlag(installedStorageKey));

  const markPwaInstalled = useCallback(() => {
    writeInstalledFlag(installedStorageKey);
    setInstalledViaPrompt(true);
  }, [installedStorageKey]);

  useEffect(() => {
    window.addEventListener('appinstalled', markPwaInstalled);
    return () => {
      window.removeEventListener('appinstalled', markPwaInstalled);
    };
  }, [markPwaInstalled]);

  const isPwaInstalled = isStandaloneDisplayMode(displayMode) || installedViaPrompt;

  return { isPwaInstalled, markPwaInstalled };
};
